// V2 Tempo — Victory Stats Burst. A short cinematic overlay that fires
// between the VICTORY/DEFEAT announcer banner and the EndOfMatch modal
// landing. Sells the win/loss as MEMORABLE by playing the player's own
// numbers back to them in a cascading sequence.
//
// CrazyGamesTricks §5.3 + §5.10: "celebrate the win loudly" + "put the
// rematch in the emotional peak." Without this, the EOM modal lands as
// a dry stats card; with it, the player sees their accomplishment first
// (12 relays, 8 squads, 14 kills) THEN the modal lands underneath as a
// continuation, not an interruption.
//
// Three numbers are cascaded ~250ms apart, each scaling up + fading in:
//   1. Relays captured
//   2. Squads built
//   3. Enemy units destroyed
//
// Pure presentation (Rule 1). DOM-based, sits at z-index 55 — above the
// announcer banner (35) and below the EOM modal (40+ for the card,
// 50 for the result-tier announcer). Auto-clears at the end of the
// cascade.

import type { MatchState, PlayerId } from '@signalclash/shared';

const ROOT_ID = 'victory-burst';
const STYLE_ID = 'victory-burst-styles';
const STAGGER_MS = 280;
const SHOW_PER_LINE_MS = 1200;

export interface VictoryStats {
  relaysHeld: number;
  squadsBuilt: number;
  enemyKills: number;
  won: boolean;
}

export class VictoryBurst {
  private root: HTMLDivElement;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    injectStyles();
    this.root = ensureRoot();
    this.root.style.display = 'none';
  }

  // Trigger the cascade. Returns the total ms the burst occupies the
  // screen — the caller (ClientApp / EndOfMatch.scheduleShow) uses this
  // to time the EOM modal so it lands AFTER the burst peaks.
  show(stats: VictoryStats): number {
    this.clear();
    this.root.innerHTML = '';
    this.root.classList.remove('victory-burst--win', 'victory-burst--loss');
    this.root.classList.add(stats.won ? 'victory-burst--win' : 'victory-burst--loss');
    const lines: ReadonlyArray<readonly [string, number, string]> = [
      ['RELAYS CAPTURED', stats.relaysHeld, '#6fdcff'],
      ['SQUADS BUILT', stats.squadsBuilt, '#ffe680'],
      ['ENEMIES DESTROYED', stats.enemyKills, '#ff7a5a'],
    ];
    const stage = document.createElement('div');
    stage.className = 'victory-burst__stage';
    for (let i = 0; i < lines.length; i++) {
      const [label, value, color] = lines[i]!;
      const row = document.createElement('div');
      row.className = 'victory-burst__row';
      row.style.animationDelay = `${i * STAGGER_MS}ms`;
      row.style.setProperty('--vb-color', color);
      row.innerHTML = `
        <span class="victory-burst__value">${value}</span>
        <span class="victory-burst__label">${label}</span>
      `;
      stage.appendChild(row);
    }
    this.root.appendChild(stage);
    this.root.style.display = 'flex';

    const total = lines.length * STAGGER_MS + SHOW_PER_LINE_MS;
    this.timer = setTimeout(() => {
      this.root.style.display = 'none';
      this.timer = null;
    }, total);
    return total;
  }

  // External clear, e.g. if the player hits Menu mid-burst.
  clear(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.root.style.display = 'none';
  }
}

// Compute the burst stats from a final snapshot + viewer kill tally.
// Tolerant to missing fields — never throws.
export function deriveVictoryStats(opts: {
  finalState: MatchState | null;
  viewerId: PlayerId;
  viewerKills: number;
  won: boolean;
}): VictoryStats {
  const { finalState, viewerId, viewerKills, won } = opts;
  let relaysHeld = 0;
  let squadsBuilt = 0;
  if (finalState !== null) {
    for (const node of Object.values(finalState.controlNodes)) {
      if (node.ownerId === viewerId) relaysHeld += 1;
    }
    squadsBuilt = finalState.stats?.squadsBuilt?.[viewerId] ?? 0;
  }
  return {
    relaysHeld,
    squadsBuilt,
    enemyKills: Math.max(0, viewerKills),
    won,
  };
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 36; /* above announcer (35), below EOM modal (40) */
  pointer-events: none;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.victory-burst__stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 36px;
}
.victory-burst__row {
  display: flex;
  align-items: baseline;
  gap: 18px;
  opacity: 0;
  transform: translateY(20px) scale(0.92);
  animation: victory-burst-row 900ms cubic-bezier(0.16, 0.84, 0.44, 1) forwards;
  filter: drop-shadow(0 0 24px rgba(0,0,0,0.55));
}
.victory-burst__value {
  font-size: 64px;
  font-weight: 700;
  color: var(--vb-color);
  letter-spacing: 0.04em;
  text-shadow:
    0 0 12px var(--vb-color),
    0 0 28px rgba(0,0,0,0.85);
  min-width: 90px;
  text-align: right;
}
.victory-burst__label {
  font-size: 18px;
  font-weight: 700;
  color: #eef4ff;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  text-shadow: 0 0 10px rgba(0,0,0,0.85);
}
@keyframes victory-burst-row {
  0% {
    opacity: 0;
    transform: translateY(24px) scale(0.85);
  }
  55% {
    opacity: 1;
    transform: translateY(0) scale(1.08);
  }
  72% {
    transform: translateY(0) scale(1.0);
  }
  85% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-12px) scale(1.0);
  }
}
@media (max-width: 640px) {
  .victory-burst__value { font-size: 44px; min-width: 64px; }
  .victory-burst__label { font-size: 14px; letter-spacing: 0.20em; }
}
`;
  document.head.appendChild(style);
}
